import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Pagination,
  Stack,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Checkbox,
  FormControlLabel,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Pending as PendingIcon,
  HourglassEmpty as HourglassEmptyIcon,
  AccountBalance as AccountBalanceIcon,
  Sync as SyncIcon,
  Warning as WarningIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import apiClient from '../lib/api';
import { toast } from 'react-hot-toast';

const TransfersList = ({ searchParams: searchParamsProp }) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [transfers, setTransfers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    pages: 0,
    hasNext: false,
    hasPrev: false,
  });
  const [reconcileModalOpen, setReconcileModalOpen] = useState(false);
  const [selectedPaymentId, setSelectedPaymentId] = useState(null);
  const [reconciliationData, setReconciliationData] = useState(null);
  const [loadingReconciliation, setLoadingReconciliation] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [retryOptions, setRetryOptions] = useState({
    retryFuneralDirector: false,
    retryDesigners: [],
  });

  const loadTransfers = async (page = pagination.page) => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiClient.getTransfers({ 
        page,
        limit: pagination.limit 
      });
      if (response.success) {
        setTransfers(response.transfers || []);
        if (response.pagination) {
          setPagination({
            page: response.pagination.page || page,
            limit: response.pagination.limit || 10,
            total: response.pagination.total || 0,
            pages: response.pagination.pages || 0,
            hasNext: response.pagination.hasNext || false,
            hasPrev: response.pagination.hasPrev || false,
          });
        }
      } else {
        throw new Error(response.message || 'Failed to load transfers');
      }
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to load transfers';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTransfers(1);
  }, []);

  // Auto-open reconciliation modal if paymentId is in URL
  useEffect(() => {
    const paymentId = searchParams.get('paymentId');
    if (paymentId && !reconcileModalOpen && selectedPaymentId !== paymentId) {
      handleOpenReconcile(paymentId);
      // Remove paymentId from URL after opening
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('paymentId');
      setSearchParams(newParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const handlePageChange = (event, newPage) => {
    loadTransfers(newPage);
  };

  const handleOpenReconcile = async (paymentId) => {
    setSelectedPaymentId(paymentId);
    setReconcileModalOpen(true);
    setLoadingReconciliation(true);
    setRetryOptions({
      retryFuneralDirector: false,
      retryDesigners: [],
    });

    try {
      const response = await apiClient.getTransferReconciliation(paymentId);
      if (response.success) {
        setReconciliationData(response.reconciliation);
        // Pre-select failed transfers for retry (only if status is partial/failed)
        if (response.reconciliation.needsReconciliation) {
          const options = {
            retryFuneralDirector: response.reconciliation.funeralDirector.canRetry,
            retryDesigners: response.reconciliation.designers
              .filter(d => d.canRetry)
              .map(d => d.designerId),
          };
          setRetryOptions(options);
        }
      } else {
        toast.error(response.message || 'Failed to load reconciliation details');
      }
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || 'Failed to load reconciliation details');
    } finally {
      setLoadingReconciliation(false);
    }
  };

  const handleCloseReconcile = () => {
    setReconcileModalOpen(false);
    setSelectedPaymentId(null);
    setReconciliationData(null);
    setRetryOptions({
      retryFuneralDirector: false,
      retryDesigners: [],
    });
  };

  const handleRetryTransfers = async () => {
    if (!selectedPaymentId) return;

    setRetrying(true);
    try {
      const response = await apiClient.retryFailedTransfers(selectedPaymentId, retryOptions);
      if (response.success) {
        toast.success(`Retried ${response.results.retried.length} transfer(s)`);
        if (response.results.failed.length > 0) {
          toast.error(`${response.results.failed.length} transfer(s) failed to retry`);
        }
        // Reload transfers and reconciliation data
        await loadTransfers(pagination.page);
        await handleOpenReconcile(selectedPaymentId);
      } else {
        toast.error(response.message || 'Failed to retry transfers');
      }
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || 'Failed to retry transfers');
    } finally {
      setRetrying(false);
    }
  };

  const handleToggleDesignerRetry = (designerId) => {
    setRetryOptions(prev => ({
      ...prev,
      retryDesigners: prev.retryDesigners.includes(designerId)
        ? prev.retryDesigners.filter(id => id !== designerId)
        : [...prev.retryDesigners, designerId],
    }));
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'partial':
        return 'warning';
      case 'failed':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircleIcon fontSize="small" />;
      case 'partial':
        return <HourglassEmptyIcon fontSize="small" />;
      case 'failed':
        return <ErrorIcon fontSize="small" />;
      default:
        return <PendingIcon fontSize="small" />;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleString();
    } catch {
      return dateString;
    }
  };

  const formatCurrency = (amount) => {
    if (!amount) return 'N/A';
    return `$${(amount / 100).toFixed(2)}`;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert 
        severity="error" 
        action={
          <IconButton onClick={loadTransfers} size="small">
            <RefreshIcon />
          </IconButton>
        }
      >
        {error}
      </Alert>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" component="h2" sx={{ fontWeight: 600 }}>
          Payment Transfers
        </Typography>
        <Tooltip title="Refresh">
          <IconButton onClick={loadTransfers} color="primary">
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {transfers.length === 0 && !loading ? (
        <Alert severity="info">No transfers found.</Alert>
      ) : (
        <>
          <TableContainer component={Paper} elevation={1}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell><strong>Payment ID</strong></TableCell>
                  <TableCell><strong>Status</strong></TableCell>
                  <TableCell><strong>FD Transfer</strong></TableCell>
                  <TableCell><strong>FD Amount</strong></TableCell>
                  <TableCell><strong>Designer Transfers</strong></TableCell>
                  <TableCell><strong>Total Amount</strong></TableCell>
                  <TableCell><strong>Card Count</strong></TableCell>
                  <TableCell><strong>Created</strong></TableCell>
                  <TableCell><strong>Updated</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {transfers.map((transfer) => (
                  <TableRow 
                    key={transfer.paymentId} 
                    hover
                    onClick={() => handleOpenReconcile(transfer.paymentId)}
                    sx={{
                      cursor: 'pointer',
                    }}
                  >
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
                      {transfer.paymentId}
                    </TableCell>
                    <TableCell>
                      <Chip
                        icon={getStatusIcon(transfer.status)}
                        label={transfer.status || 'unknown'}
                        color={getStatusColor(transfer.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {transfer.funeralDirectorTransfer ? (
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                          {transfer.funeralDirectorTransfer}
                        </Typography>
                      ) : (
                        <Chip label="None" size="small" color="default" />
                      )}
                    </TableCell>
                    <TableCell>
                      {transfer.funeralDirectorAmount ? formatCurrency(transfer.funeralDirectorAmount) : 'N/A'}
                    </TableCell>
                    <TableCell>
                      {transfer.designerTransfers && transfer.designerTransfers.length > 0 ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          {transfer.designerTransfers.map((dt, idx) => (
                            <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Typography variant="caption" sx={{ fontFamily: 'monospace', fontSize: '0.7rem' }}>
                                {dt.transferId || 'N/A'}
                              </Typography>
                              <Chip 
                                label={formatCurrency(dt.amount)} 
                                size="small" 
                                color={dt.transferId ? 'success' : 'error'}
                                sx={{ fontSize: '0.65rem', height: 18 }}
                              />
                            </Box>
                          ))}
                        </Box>
                      ) : (
                        <Chip label="None" size="small" color="default" />
                      )}
                    </TableCell>
                    <TableCell>
                      {transfer.totalAmount ? formatCurrency(transfer.totalAmount) : 'N/A'}
                    </TableCell>
                    <TableCell>
                      {transfer.cardCount || 0}
                    </TableCell>
                    <TableCell>{formatDate(transfer.createdAt)}</TableCell>
                    <TableCell>{formatDate(transfer.updatedAt)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          
          {pagination.pages > 0 && (
            <Stack spacing={2} sx={{ mt: 3, alignItems: 'center' }}>
              <Pagination
                count={pagination.pages}
                page={pagination.page}
                onChange={handlePageChange}
                color="primary"
                showFirstButton
                showLastButton
              />
              <Typography variant="body2" color="text.secondary">
                Showing {transfers.length} of {pagination.total} transfers
              </Typography>
            </Stack>
          )}
        </>
      )}

      {/* Reconciliation Modal */}
      <Dialog
        open={reconcileModalOpen}
        onClose={handleCloseReconcile}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SyncIcon color="warning" />
            <Typography variant="h6">
              {reconciliationData?.currentStatus === 'completed' ? 'Transfer Details' : 'Reconcile Transfer'}
            </Typography>
            {selectedPaymentId && (
              <Typography variant="caption" sx={{ fontFamily: 'monospace', ml: 2, color: 'text.secondary' }}>
                {selectedPaymentId}
              </Typography>
            )}
          </Box>
        </DialogTitle>
        <DialogContent>
          {loadingReconciliation ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : reconciliationData ? (
            <Box>
              <Alert severity={reconciliationData.needsReconciliation ? 'warning' : 'success'} sx={{ mb: 3 }}>
                <Typography variant="body2">
                  <strong>Status:</strong> {reconciliationData.currentStatus}
                  {reconciliationData.needsReconciliation && ' - Needs Reconciliation'}
                </Typography>
              </Alert>

              {reconciliationData.order && (
                <>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      Associated Order
                    </Typography>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<OpenInNewIcon />}
                      onClick={() => {
                        navigate(`/admin-tools?tool=orders&orderId=${reconciliationData.order.paymentIntentId}`);
                        handleCloseReconcile();
                      }}
                    >
                      View Order
                    </Button>
                  </Box>
                  <Box sx={{ mb: 3, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Order ID:</strong>{' '}
                      <span style={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
                        {reconciliationData.order.paymentIntentId}
                      </span>
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Status:</strong>{' '}
                      <Chip
                        label={reconciliationData.order.status}
                        size="small"
                        color={
                          reconciliationData.order.status === 'completed' ? 'success' :
                          reconciliationData.order.status === 'failed' ? 'error' : 'warning'
                        }
                      />
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Amount:</strong>{' '}
                      {reconciliationData.order.amount && Number.isFinite(reconciliationData.order.amount)
                        ? formatCurrency(reconciliationData.order.amount)
                        : 'N/A'}{' '}
                      {reconciliationData.order.currency && (
                        <span style={{ textTransform: 'uppercase' }}>
                          {reconciliationData.order.currency}
                        </span>
                      )}
                    </Typography>
                    {reconciliationData.order.obituaryId && (
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>Obituary ID:</strong>{' '}
                        <span style={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
                          {reconciliationData.order.obituaryId}
                        </span>
                      </Typography>
                    )}
                    {reconciliationData.order.executionArn && (
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>Execution ARN:</strong>{' '}
                        <span style={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                          {reconciliationData.order.executionArn}
                        </span>
                      </Typography>
                    )}
                    <Typography variant="body2" color="text.secondary">
                      <strong>Created:</strong> {formatDate(reconciliationData.order.createdAt)}
                    </Typography>
                  </Box>
                  <Divider sx={{ my: 3 }} />
                </>
              )}

              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                Summary
              </Typography>
              <Box sx={{ mb: 3, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                <Typography variant="body2">
                  <strong>Total Expected:</strong> {formatCurrency(reconciliationData.summary.totalExpected)}
                </Typography>
                <Typography variant="body2">
                  <strong>Succeeded:</strong> {reconciliationData.summary.succeeded}
                </Typography>
                <Typography variant="body2" color="error">
                  <strong>Failed:</strong> {reconciliationData.summary.failed}
                </Typography>
              </Box>

              <Divider sx={{ my: 3 }} />

              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                Funeral Director Transfer
              </Typography>
              <Box sx={{ mb: 3, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="body2">
                    <strong>Amount:</strong> {formatCurrency(reconciliationData.funeralDirector.expectedAmount)}
                  </Typography>
                  <Chip
                    label={reconciliationData.funeralDirector.status}
                    size="small"
                    color={
                      reconciliationData.funeralDirector.status === 'completed' ? 'success' :
                      reconciliationData.funeralDirector.status === 'failed' ? 'error' : 'warning'
                    }
                  />
                </Box>
                {reconciliationData.funeralDirector.transferId && (
                  <Typography variant="caption" sx={{ fontFamily: 'monospace', display: 'block', mt: 1 }}>
                    Transfer ID: {reconciliationData.funeralDirector.transferId}
                  </Typography>
                )}
                {reconciliationData.funeralDirector.canRetry && (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={retryOptions.retryFuneralDirector}
                        onChange={(e) => setRetryOptions(prev => ({ ...prev, retryFuneralDirector: e.target.checked }))}
                      />
                    }
                    label="Retry Funeral Director Transfer"
                    sx={{ mt: 1 }}
                  />
                )}
              </Box>

              <Divider sx={{ my: 3 }} />

              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                Designer Transfers
              </Typography>
              <List>
                {reconciliationData.designers.map((designer, idx) => (
                  <ListItem
                    key={idx}
                    sx={{
                      bgcolor: 'background.default',
                      borderRadius: 1,
                      mb: 1,
                      border: '1px solid',
                      borderColor: designer.canRetry ? 'warning.main' : 'divider',
                    }}
                  >
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="body2">
                            <strong>Designer:</strong> {designer.designerId}
                          </Typography>
                          <Chip
                            label={designer.status}
                            size="small"
                            color={
                              designer.status === 'completed' ? 'success' :
                              designer.status === 'failed' ? 'error' : 'warning'
                            }
                          />
                        </Box>
                      }
                      secondary={
                        <Box sx={{ mt: 1 }}>
                          <Typography variant="body2">
                            <strong>Amount:</strong> {formatCurrency(designer.expectedAmount)}
                          </Typography>
                          {designer.transferId && (
                            <Typography variant="caption" sx={{ fontFamily: 'monospace', display: 'block' }}>
                              Transfer ID: {designer.transferId}
                            </Typography>
                          )}
                          {designer.error && (
                            <Typography variant="caption" color="error" sx={{ display: 'block' }}>
                              Error: {designer.error}
                            </Typography>
                          )}
                        </Box>
                      }
                    />
                    {designer.canRetry && (
                      <ListItemSecondaryAction>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={retryOptions.retryDesigners.includes(designer.designerId)}
                              onChange={() => handleToggleDesignerRetry(designer.designerId)}
                            />
                          }
                          label="Retry"
                        />
                      </ListItemSecondaryAction>
                    )}
                  </ListItem>
                ))}
              </List>

              {reconciliationData.errors && reconciliationData.errors.length > 0 && (
                <>
                  <Divider sx={{ my: 3 }} />
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                    Errors
                  </Typography>
                  <Box sx={{ p: 2, bgcolor: 'error.light', borderRadius: 1 }}>
                    {reconciliationData.errors.map((error, idx) => (
                      <Typography key={idx} variant="body2" sx={{ mb: 0.5 }}>
                        • {error}
                      </Typography>
                    ))}
                  </Box>
                </>
              )}
            </Box>
          ) : (
            <Alert severity="error">Failed to load reconciliation details</Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseReconcile} disabled={retrying}>
            Close
          </Button>
          {reconciliationData?.needsReconciliation && (
            <Button
              variant="contained"
              color="warning"
              onClick={handleRetryTransfers}
              disabled={retrying || (!retryOptions.retryFuneralDirector && retryOptions.retryDesigners.length === 0)}
              startIcon={retrying ? <CircularProgress size={20} /> : <SyncIcon />}
            >
              {retrying ? 'Retrying...' : 'Retry Selected Transfers'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TransfersList;
